// Layouts/Assignments/parts/BulkGradeModal.tsx
import React from 'react';
import { Submission } from './GradableSubmissionCard';

const C = {
  navy: '#0e225d',
  gold: '#fbb21f',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  bg: '#F8FAFC',
};

interface GradeData {
  marks_obtained: string;
  total_marks: string;
  feedback: string;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  selectedSubmissions: Set<string>;
  submissions: Submission[];
  bulkGradeData: { [key: string]: GradeData };
  onUpdateGradeField: (id: string, field: keyof GradeData, value: string) => void;
  onSubmit: () => void;
  isGrading: boolean;
}

/**
 * Web port of components/assignments/teacher/BulkGradeModal.tsx (RN).
 * Same field names / bulk-grade payload shape; Alert.prompt -> window.prompt,
 * RN Modal -> fixed overlay (matches App.tsx's LogoutModal pattern).
 */
const BulkGradeModal: React.FC<Props> = ({
  visible, onClose, selectedSubmissions, submissions,
  bulkGradeData, onUpdateGradeField, onSubmit, isGrading,
}) => {
  if (!visible) return null;

  const applyUniformMarks = () => {
    const totalMarks = window.prompt('Enter total marks to apply to all selected submissions:');
    if (totalMarks && !isNaN(Number(totalMarks))) {
      selectedSubmissions.forEach(id => onUpdateGradeField(id, 'total_marks', totalMarks));
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '20px',
    }}>
      <div style={{
        background: C.white, borderRadius: '18px', width: '100%', maxWidth: '560px',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '18px 22px', borderBottom: `1px solid ${C.border}`,
        }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: C.navy }}>
            Bulk Grade Submissions
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: C.navy }}
          >
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: C.sub, lineHeight: '1.5' }}>
            You are about to grade {selectedSubmissions.size} submission(s). Please review the marks and feedback before submitting.
          </p>

          <button
            onClick={applyUniformMarks}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              width: '100%', padding: '10px', marginBottom: '16px',
              background: 'rgba(14,34,93,0.06)', border: `1px solid ${C.navy}`,
              borderRadius: '10px', color: C.navy, fontSize: '0.82rem', fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <i className="pi pi-copy" style={{ fontSize: '0.85rem' }} />
            Apply Uniform Total Marks
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Array.from(selectedSubmissions).map((submissionId) => {
              const submission = submissions.find(s => s.submission_id === submissionId);
              if (!submission) return null;
              const data = bulkGradeData[submissionId] || { marks_obtained: '', total_marks: '', feedback: '' };

              return (
                <div key={submissionId} style={{ background: C.bg, padding: '14px', borderRadius: '12px', border: `1px solid ${C.border}` }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: 700, color: C.navy, fontSize: '0.88rem' }}>
                    {submission.student_name}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.72rem', color: C.sub, marginBottom: '5px' }}>Obtained</label>
                      <input
                        type="number"
                        value={data.marks_obtained}
                        onChange={(e) => onUpdateGradeField(submissionId, 'marks_obtained', e.target.value)}
                        placeholder="0"
                        style={{
                          width: '100%', padding: '9px', borderRadius: '8px',
                          border: `1px solid ${C.border}`, fontSize: '0.95rem', fontWeight: 600,
                          color: C.navy, textAlign: 'center', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <span style={{ paddingBottom: '9px', color: C.sub, fontWeight: 700 }}>/</span>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.72rem', color: C.sub, marginBottom: '5px' }}>Total</label>
                      <input
                        type="number"
                        value={data.total_marks}
                        onChange={(e) => onUpdateGradeField(submissionId, 'total_marks', e.target.value)}
                        placeholder="0"
                        style={{
                          width: '100%', padding: '9px', borderRadius: '8px',
                          border: `1px solid ${C.border}`, fontSize: '0.95rem', fontWeight: 600,
                          color: C.navy, textAlign: 'center', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>

                  <label style={{ display: 'block', fontSize: '0.72rem', color: C.sub, marginBottom: '5px' }}>Feedback (Optional)</label>
                  <textarea
                    value={data.feedback}
                    onChange={(e) => onUpdateGradeField(submissionId, 'feedback', e.target.value)}
                    placeholder="Enter feedback..."
                    rows={2}
                    style={{
                      width: '100%', padding: '9px', borderRadius: '8px',
                      border: `1px solid ${C.border}`, fontSize: '0.82rem', color: C.navy,
                      resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '12px', padding: '16px 22px', borderTop: `1px solid ${C.border}` }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
              background: '#F3F4F6', color: C.navy, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={isGrading}
            style={{
              flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '12px', borderRadius: '10px', border: `2px solid ${C.gold}`,
              background: C.navy, color: C.white, fontWeight: 700, fontSize: '0.85rem',
              cursor: isGrading ? 'not-allowed' : 'pointer', opacity: isGrading ? 0.6 : 1,
            }}
          >
            {isGrading ? (
              <i className="pi pi-spin pi-spinner" />
            ) : (
              <>
                <i className="pi pi-check-circle" />
                Grade {selectedSubmissions.size} Submissions
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkGradeModal;
